<template>
	<div ref="transitionWrapper" class="elements-transition-wrapper" :style="wrapperStyle">
		<transition
			:name="transitionName"
			mode="out-in"
			@before-enter="beforeEnter"
			@enter="enter"
			@after-enter="afterEnter"
			@before-leave="beforeLeave"
			@leave="leave"
			@after-leave="afterLeave"
		>
			<slot />
		</transition>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"

type TransitionType = "fade" | "fade-left" | "fade-right" | "fade-up" | "fade-down" | "left" | "right" | "up" | "down"

const props = defineProps<{
	type?: TransitionType
	duration?: number
}>()

const type = computed(() => props.type || "fade")
const duration = computed(() => props.duration || 250)

const transitionWrapper = ref<HTMLElement>()
const previousHeight = ref<number>(0)

const transitionName = computed(() => `elements-transition-${type.value}`)

const wrapperStyle = computed(() => ({
	transition: `height ${duration.value}ms ease-in-out`,
	"--transition-duration": `${duration.value}ms`
}))

function beforeLeave(el: Element) {
	const element = el as HTMLElement
	if (transitionWrapper.value) {
		const height = element.scrollHeight
		transitionWrapper.value.style.height = `${height}px`
		previousHeight.value = height
	}
}

function leave(el: Element, done: () => void) {
	setTimeout(() => {
		done()
	}, duration.value)
}

function afterLeave() {
	// L'altezza viene mantenuta dal wrapper
}

function beforeEnter() {
	if (transitionWrapper.value && previousHeight.value > 0) {
		transitionWrapper.value.style.height = `${previousHeight.value}px`
	}
}

function enter(el: Element, done: () => void) {
	const element = el as HTMLElement
	const targetHeight = element.scrollHeight

	if (transitionWrapper.value) {
		requestAnimationFrame(() => {
			if (transitionWrapper.value) {
				transitionWrapper.value.style.height = `${targetHeight}px`
			}
		})
	}

	setTimeout(() => {
		done()
	}, duration.value)
}

function afterEnter(el: Element) {
	const element = el as HTMLElement
	if (transitionWrapper.value) {
		transitionWrapper.value.style.height = "auto"
		previousHeight.value = element.scrollHeight
	}
}
</script>

<style lang="scss" scoped>
.elements-transition-wrapper {
	overflow: hidden;
}

:deep(.elements-transition-fade-enter-active),
:deep(.elements-transition-fade-leave-active),
:deep(.elements-transition-fade-left-enter-active),
:deep(.elements-transition-fade-left-leave-active),
:deep(.elements-transition-fade-right-enter-active),
:deep(.elements-transition-fade-right-leave-active),
:deep(.elements-transition-fade-up-enter-active),
:deep(.elements-transition-fade-up-leave-active),
:deep(.elements-transition-fade-down-enter-active),
:deep(.elements-transition-fade-down-leave-active),
:deep(.elements-transition-left-enter-active),
:deep(.elements-transition-left-leave-active),
:deep(.elements-transition-right-enter-active),
:deep(.elements-transition-right-leave-active),
:deep(.elements-transition-up-enter-active),
:deep(.elements-transition-up-leave-active),
:deep(.elements-transition-down-enter-active),
:deep(.elements-transition-down-leave-active) {
	transition:
		opacity var(--transition-duration, 0.25s) ease-in-out,
		transform var(--transition-duration, 0.25s) ease-in-out;
}

:deep(.elements-transition-fade-enter-from) {
	opacity: 0;
}

:deep(.elements-transition-fade-leave-to) {
	opacity: 0;
}

:deep(.elements-transition-fade-left-enter-from) {
	opacity: 0;
	transform: translateX(10px);
}

:deep(.elements-transition-fade-left-leave-to) {
	opacity: 0;
	transform: translateX(-10px);
}

:deep(.elements-transition-fade-right-enter-from) {
	opacity: 0;
	transform: translateX(-10px);
}

:deep(.elements-transition-fade-right-leave-to) {
	opacity: 0;
	transform: translateX(10px);
}

:deep(.elements-transition-fade-up-enter-from) {
	opacity: 0;
	transform: translateY(10px);
}

:deep(.elements-transition-fade-up-leave-to) {
	opacity: 0;
	transform: translateY(-10px);
}

:deep(.elements-transition-fade-down-enter-from) {
	opacity: 0;
	transform: translateY(-10px);
}

:deep(.elements-transition-fade-down-leave-to) {
	opacity: 0;
	transform: translateY(10px);
}

:deep(.elements-transition-left-enter-from) {
	transform: translateX(10px);
}

:deep(.elements-transition-left-leave-to) {
	transform: translateX(-10px);
}

:deep(.elements-transition-right-enter-from) {
	transform: translateX(-10px);
}

:deep(.elements-transition-right-leave-to) {
	transform: translateX(10px);
}

:deep(.elements-transition-up-enter-from) {
	transform: translateY(10px);
}

:deep(.elements-transition-up-leave-to) {
	transform: translateY(-10px);
}

:deep(.elements-transition-down-enter-from) {
	transform: translateY(-10px);
}

:deep(.elements-transition-down-leave-to) {
	transform: translateY(10px);
}
</style>
